/**
 * THE FOUR DIMENSIONS THAT ARE NOT ABOUT A CHANNEL.
 *
 * <pre>
 *    1  bar analysis summary         what the scanned range IS
 *   10  Fibonacci completion         which retracement depths a swing actually reaches
 *   14  volume profile & liquidity   dry-up against expansion, as a FILTER rather than a chart
 *   15  edge score                   a composite, CALIBRATED ON THIS SCAN
 * </pre>
 */

import { detectPivots, PIVOT_HIGH } from './pivots';
import { FIB_RATIOS } from './technicalDrawings';
import { BAR_CLOSE, BAR_HIGH, BAR_LOW, BAR_OPEN } from './analyticaGeometry';
import { mean, median, pct, rate, stdev, wilson } from './analyticaStats';

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** The window a relative-volume reading is measured against. Twenty sessions — a trading month. */
export const RVOL_WINDOW = 20;

/** Above this, volume EXPANDED; below the dry-up threshold, it dried up. */
export const RVOL_EXPANSION = 1.3;
export const RVOL_DRY_UP = 0.8;

/* ────────────────────────────────── 1. BAR ANALYSIS SUMMARY ───────────────────────────────── */

/**
 * What the scanned range is, before anything is claimed about it.
 *
 * <p>The trend state is a 50/200 close crossover, which is the same pair every macro gate in this
 * repository uses — so "Accumulation" here means what it means in the engine rather than being a
 * fourth definition of a trend. UNKNOWN below 200 bars rather than a guess, which is also that
 * gate's own rule: the quantity does not exist yet, and reporting a regime from 40 bars would be
 * reporting the shape of the warm-up.
 */
export function analyseBars(dates, ohlc, volumes, upToIndex) {
  const last = n(upToIndex);
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const axis = Array.isArray(dates) ? dates : [];
  const vols = Array.isArray(volumes) ? volumes : [];
  if (last === null || last < 0 || bars.length === 0) return null;

  const to = Math.min(last, bars.length - 1);
  let high = -Infinity;
  let low = Infinity;
  let highAt = -1;
  let lowAt = -1;
  let up = 0;
  let down = 0;
  const closes = [];
  const ranges = [];
  const volSeen = [];

  for (let i = 0; i <= to; i++) {
    const bar = bars[i];
    if (!bar) continue;
    const h = n(bar[BAR_HIGH]);
    const l = n(bar[BAR_LOW]);
    const c = n(bar[BAR_CLOSE]);
    const o = n(bar[BAR_OPEN]);
    if (h !== null && h > high) { high = h; highAt = i; }
    if (l !== null && l < low) { low = l; lowAt = i; }
    if (c !== null) closes.push(c);
    if (h !== null && l !== null) ranges.push(h - l);
    if (c !== null && o !== null) { if (c > o) up += 1; else if (c < o) down += 1; }
    const v = n(vols[i]);
    if (v !== null) volSeen.push(v);
  }

  const first = n(bars[0]?.[BAR_CLOSE]);
  const lastClose = n(bars[to]?.[BAR_CLOSE]);

  return {
    bars: to + 1,
    firstDate: axis[0] || null,
    lastDate: axis[to] || null,
    high: Number.isFinite(high) ? high : null,
    highDate: highAt >= 0 ? axis[highAt] : null,
    low: Number.isFinite(low) ? low : null,
    lowDate: lowAt >= 0 ? axis[lowAt] : null,
    range: Number.isFinite(high) && Number.isFinite(low) ? high - low : null,
    firstClose: first,
    lastClose,
    totalReturnPct: first !== null && lastClose !== null && first !== 0
      ? ((lastClose - first) / first) * 100 : null,
    upBars: up,
    downBars: down,
    dojiBars: (to + 1) - up - down,
    averageVolume: mean(volSeen),
    medianVolume: median(volSeen),
    averageRange: mean(ranges),
    closeStdev: stdev(closes),
    trend: trendState(bars, to),
  };
}

/** The 50/200 close crossover, UNKNOWN until both averages exist. */
function trendState(bars, at) {
  const sma = (period) => {
    if (at + 1 < period) return null;
    let sum = 0;
    for (let i = at - period + 1; i <= at; i++) {
      const c = n(bars[i]?.[BAR_CLOSE]);
      if (c === null) return null;
      sum += c;
    }
    return sum / period;
  };
  const fast = sma(50);
  const slow = sma(200);
  if (fast === null || slow === null) return { state: 'UNKNOWN', fast, slow };
  if (fast > slow) return { state: 'ACCUMULATION', fast, slow };
  if (fast < slow) return { state: 'DISTRIBUTION', fast, slow };
  // Exactly equal is a real crossover bar, not a missing reading.
  return { state: 'NEUTRAL', fast, slow };
}

/* ─────────────────────────────── 10. FIBONACCI COMPLETION ─────────────────────────────────── */

/**
 * How deep each swing's retracement actually got, and which standard ratios that completed.
 *
 * <h2>A swing is two CONSECUTIVE opposite pivots</h2>
 *
 * Low to high is a bullish impulse; high to low a bearish one. Consecutive, because pairing a high
 * with the low three swings back measures a move no reader would draw.
 *
 * <h2>The retracement is measured until the swing EXTENDS or is INVALIDATED</h2>
 *
 * Walking forward from the impulse's end: the window closes when price makes a new extreme in the
 * impulse's direction (the move resumed, so the retracement is over at whatever depth it reached)
 * or when it gives back more than 100% (the swing is gone). Without a terminating condition every
 * swing eventually "completes" 100% and the dimension measures nothing but how long the chart is.
 *
 * <h2>Depth uses the EXTREME, and the ratios come from the drawing tool</h2>
 *
 * A retracement is tested by where price TRADED, not where it closed — the same reading the
 * reversion tests use. `FIB_RATIOS` is imported from `technicalDrawings` rather than retyped, so
 * the levels this dimension scores are the levels the Fibonacci tool draws.
 */
export function analyseFibonacci(ohlc, upToIndex, pivotStrength) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const last = n(upToIndex);
  if (last === null || last < 2) return { swings: 0, ratios: [], rows: [] };

  const scope = bars.slice(0, last + 1);
  const { all } = detectPivots(scope, pivotStrength);
  const rows = [];

  for (let k = 1; k < all.length; k++) {
    const a = all[k - 1];
    const b = all[k];
    if (a.type === b.type) continue;                // not a swing — two tops with no trough named
    const bullish = b.type === PIVOT_HIGH;
    const span = Math.abs(b.price - a.price);
    if (!(span > 0)) continue;

    let deepest = 0;
    let closedAt = null;
    let reason = 'scan ended';
    for (let i = b.index + 1; i <= last; i++) {
      const bar = scope[i];
      if (!bar) continue;
      const high = n(bar[BAR_HIGH]);
      const low = n(bar[BAR_LOW]);
      if (high === null || low === null) continue;

      // EXTENDED: a new extreme the impulse's way ends the retracement window.
      if (bullish ? high > b.price : low < b.price) { closedAt = i; reason = 'extended'; break; }

      const given = bullish ? (b.price - low) : (high - b.price);
      const depth = given / span;
      if (depth > deepest) deepest = depth;
      if (depth >= 1) { closedAt = i; reason = 'invalidated'; break; }
    }

    rows.push({
      from: a.index,
      to: b.index,
      direction: bullish ? 'bullish' : 'bearish',
      span,
      deepest,
      closedAt,
      reason,
      // A swing still open at the clicked bar has not finished retracing, so "never reached
      // 61.8%" is provisional. Carried, and excluded from the completion rates below.
      resolved: reason !== 'scan ended',
    });
  }

  const resolved = rows.filter(r => r.resolved);
  const forDirection = (direction) => {
    const set = resolved.filter(r => r.direction === direction);
    return FIB_RATIOS.map((ratio) => {
      const hits = set.filter(r => r.deepest >= ratio).length;
      return { ratio, label: pct(ratio), ...rate(hits, set.length), ci: wilson(hits, set.length) };
    });
  };

  return {
    swings: rows.length,
    resolvedSwings: resolved.length,
    openSwings: rows.length - resolved.length,
    medianDepth: median(resolved.map(r => r.deepest)),
    bullish: forDirection('bullish'),
    bearish: forDirection('bearish'),
    rows,
  };
}

/* ────────────────────────── 14. VOLUME PROFILE & LIQUIDITY ────────────────────────────────── */

/**
 * Relative volume per bar — this bar against the twenty before it.
 *
 * <p>The window EXCLUDES the bar being judged. A spike that is a twentieth of its own denominator
 * reports as 2.0x when it is 2.1x, and the understatement is largest on exactly the bars this
 * exists to catch. The committee's own volume manager states the same rule for the same reason.
 *
 * <p>Null for the first twenty bars rather than a short-window average: a reading taken over three
 * bars is not the same quantity and would be read as though it were.
 */
export function relativeVolume(volumes, window = RVOL_WINDOW) {
  const vols = Array.isArray(volumes) ? volumes : [];
  const w = Math.max(1, Math.floor(window) || RVOL_WINDOW);
  const out = new Array(vols.length).fill(null);
  let sum = 0;
  for (let i = 0; i < vols.length; i++) {
    if (i >= w) {
      const avg = sum / w;
      const v = n(vols[i]);
      out[i] = (avg > 0 && v !== null) ? v / avg : null;
      const drop = n(vols[i - w]);
      if (drop !== null) sum -= drop;
    }
    const cur = n(vols[i]);
    sum += cur === null ? 0 : cur;
  }
  return out;
}

/**
 * What volume did on the bars that mattered, as a FILTER rather than as a picture.
 *
 * <p>The brief asks for actionable commentary on dry-ups against expansion. The actionable form
 * of that is a comparison: did the events that happened on EXPANDED volume resolve differently
 * from the ones that did not. Everything below exists to support that one sentence.
 */
export function analyseVolume({ volumes, events, ctsRows, upToIndex }) {
  const rvol = relativeVolume(volumes);
  const last = n(upToIndex);
  if (last === null) return null;

  const readable = [];
  for (let i = 0; i <= Math.min(last, rvol.length - 1); i++) {
    if (rvol[i] !== null) readable.push(rvol[i]);
  }

  const at = (i) => (i >= 0 && i < rvol.length ? rvol[i] : null);
  const evs = Array.isArray(events) ? events : [];
  const pen = evs.filter(e => e.kind === 'penetration').map(e => at(e.index)).filter(v => v !== null);
  const rev = evs.filter(e => e.kind === 'reversion').map(e => at(e.index)).filter(v => v !== null);

  const cts = (Array.isArray(ctsRows) ? ctsRows : [])
    .filter(r => r.resolved)
    .map(r => ({ ...r, rvol: at(r.index) }))
    .filter(r => r.rvol !== null);
  const expanded = cts.filter(r => r.rvol >= RVOL_EXPANSION);
  const quiet = cts.filter(r => r.rvol < RVOL_EXPANSION);
  const tally = (set) => {
    const wins = set.filter(r => r.success).length;
    return { ...rate(wins, set.length), ci: wilson(wins, set.length) };
  };

  return {
    barsRead: readable.length,
    meanRvol: mean(readable),
    penetrationRvol: mean(pen),
    reversionRvol: mean(rev),
    penetrationCount: pen.length,
    reversionCount: rev.length,
    expansionRate: rate(readable.filter(v => v >= RVOL_EXPANSION).length, readable.length),
    dryUpRate: rate(readable.filter(v => v <= RVOL_DRY_UP).length, readable.length),
    cts: {
      n: cts.length,
      expanded: { n: expanded.length, ...tally(expanded), meanRvol: mean(expanded.map(r => r.rvol)) },
      quiet: { n: quiet.length, ...tally(quiet), meanRvol: mean(quiet.map(r => r.rvol)) },
      // The LIFT is the actionable number: positive means a volume filter on CTS would have
      // helped on this chart, negative means it would have discarded the better signals.
      lift: expanded.length > 0 && quiet.length > 0
        ? (expanded.filter(r => r.success).length / expanded.length)
          - (quiet.filter(r => r.success).length / quiet.length)
        : null,
    },
    rvol,
  };
}

/* ──────────────────────────────── 15. THE EDGE SCORE ──────────────────────────────────────── */

/**
 * A composite confluence rating, 0–100, for every CTS point in the scan.
 *
 * <h2>CALIBRATED ON THIS SCAN, which is both the design and the caveat</h2>
 *
 * Each component is worth what it was MEASURED to be worth on these bars — the zone's own edge
 * over the baseline, the divergence's own lift, the volume filter's own lift — rather than a
 * weight somebody typed. So the score adapts to the instrument instead of asserting that Zone 3
 * is always best, and a component that did nothing here contributes nothing here.
 *
 * <p><b>And that makes every figure below IN-SAMPLE.</b> The score is fitted to the same points
 * it then ranks, so "high-score setups won 68%" is arithmetic, not evidence — the components were
 * chosen because they separated these very trades. It is reported anyway, with the caveat
 * attached on screen, because the per-component breakdown IS informative: a reader can see which
 * of the four carried the rating and whether any of them is large enough to be worth believing.
 *
 * <p>The honest use is as a RANKING within one chart, never as a probability.
 */
export function scoreEdge({ ctsRows, ctsAnalysis, volume }) {
  const rows = (Array.isArray(ctsRows) ? ctsRows : []);
  if (rows.length === 0) return { scored: 0, components: [], buckets: [], rows: [] };

  const zoneEdge = {};
  ['upsideByZone', 'downsideByZone'].forEach((side) => {
    (ctsAnalysis?.[side]?.zones || []).forEach((z) => {
      const key = `${side === 'upsideByZone' ? 'up' : 'down'}:${z.zone}`;
      zoneEdge[key] = n(z.edge);
    });
  });
  const divLift = {
    up: n(ctsAnalysis?.withDivergence?.up?.lift),
    down: n(ctsAnalysis?.withDivergence?.down?.lift),
  };
  const volLift = n(volume?.cts?.lift);
  const distBuckets = ctsAnalysis?.distanceVsReversion?.buckets || [];
  const distOverall = n(ctsAnalysis?.distanceVsReversion?.overall?.rate);

  // Each component contributes its own MEASURED edge in percentage points, clamped so a single
  // freak cell cannot dominate: an edge measured on four observations is not worth four times an
  // edge measured on two hundred, and the clamp is the cheapest way to say so.
  const clamp = (v) => (v === null ? 0 : Math.max(-0.25, Math.min(0.25, v)));

  const scored = rows.map((r) => {
    const zone = clamp(zoneEdge[`${r.direction}:${r.zone}`] ?? null);
    const div = r.divergenceAgrees ? clamp(divLift[r.direction]) : 0;
    const rv = volume?.rvol?.[r.index];
    const vol = (n(rv) !== null && rv >= RVOL_EXPANSION) ? clamp(volLift) : 0;
    const bucket = distBuckets.find(b => r.absDistance !== null
      && r.absDistance >= b.from && r.absDistance < b.to);
    const dist = (bucket && n(bucket.rate) !== null && distOverall !== null)
      ? clamp(bucket.rate - distOverall) : 0;

    const raw = zone + div + vol + dist;             // in [-1, 1] by the clamp
    return {
      ...r,
      rvol: n(rv),
      parts: { zone, divergence: div, volume: vol, distance: dist },
      // Mapped to 0–100 with 50 as "nothing either way", so the midpoint is legible and a score
      // below it genuinely means the measured components were against this setup.
      score: Math.round(Math.max(0, Math.min(100, 50 + raw * 50))),
    };
  });

  const resolved = scored.filter(r => r.resolved);
  const tiers = [
    { label: 'High (70+)', min: 70, max: 101 },
    { label: 'Medium (55–69)', min: 55, max: 70 },
    { label: 'Low (under 55)', min: 0, max: 55 },
  ].map((t) => {
    const set = resolved.filter(r => r.score >= t.min && r.score < t.max);
    const wins = set.filter(r => r.success).length;
    return { ...t, n: set.length, ...rate(wins, set.length), ci: wilson(wins, set.length),
      meanReturnPct: mean(set.map(r => r.returnPct)) };
  });

  return {
    scored: scored.length,
    resolved: resolved.length,
    inSample: true,
    components: [
      { key: 'zone', label: 'Zone edge over baseline', available: Object.keys(zoneEdge).length > 0 },
      { key: 'divergence', label: 'Agreeing RSI divergence',
        available: divLift.up !== null || divLift.down !== null },
      { key: 'volume', label: 'Volume expansion at the signal', available: volLift !== null },
      { key: 'distance', label: 'Distance from the equator', available: distBuckets.length > 0 },
    ],
    buckets: tiers,
    // The separation a reader should judge the whole thing on: if High and Low resolve at the
    // same rate the rating is ranking nothing, however carefully it was built.
    separation: tiers[0].rate !== null && tiers[2].rate !== null
      ? tiers[0].rate - tiers[2].rate : null,
    rows: scored,
  };
}
